<template>
    <div class="favorites-root">
        <Header :label="$t('favorites.header')" overlap="small" />

        <MainLayout marginTop>
            <ProfileModule slot="right" />

            <FeaturedResource slot="right" />

            <VerifyModule slot="right" />
            <WidgetModule slot="right" />

            <div slot="center">
                <Paper class="tab-root mb-3">
                    <Tabs v-model="section" :tabs="tabs()" />
                </Paper>

                <div v-if="isLoadingFirstTime">
                    <Spinner style="display:block;margin:auto;" />
                </div>

                <div v-else>
                    <Pagination v-show="isActiveSection" @onBottom="nextActivePage">
                        <div class="padding"></div>

                        <SectionHeader
                            v-if="expiresThisWeek.length > 0"
                            :label="$t('favorites.expiresThisWeek')"
                        />

                        <FavoriteGrid v-if="expiresThisWeek.length > 0" :data="expiresThisWeek" />

                        <SectionHeader
                            v-if="expiresThisMonth.length > 0"
                            :label="$t('favorites.expiresThisMonth')"
                        />

                        <FavoriteGrid v-if="expiresThisMonth.length > 0" :data="expiresThisMonth" />

                        <SectionHeader
                            v-if="
                                expiresLater.length > 0 &&
                                    (expiresThisWeek.length > 0 || expiresThisMonth.length > 0)
                            "
                            :label="$t('favorites.expiresLater')"
                        />

                        <FavoriteGrid v-if="expiresLater.length > 0" :data="expiresLater" />

                        <div v-if="numberOfActive === 0 && !loadingActive">
                            <NoContent :caption="$t('favorites.noActiveFavorites')"> </NoContent>
                        </div>

                        <FavoriteGridSkeleton v-if="loadingActive" />
                    </Pagination>
                    <Pagination v-show="isExpiredSection" @onBottom="nextExpiredPage">
                        <FavoriteGrid :data="expired" />

                        <div v-if="expired.length === 0 && !loadingExpired">
                            <NoContent :caption="$t('favorites.noExpired')"> </NoContent>
                        </div>

                        <FavoriteGridSkeleton v-if="loadingExpired" />
                    </Pagination>
                </div>
            </div>
        </MainLayout>
    </div>
</template>

<script>
// Components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import VerifyModule from '~/components/miscellaneous/VerifyModule';
import NoContent from '~/components/miscellaneous/NoContent';
import FavoriteGrid from '~/shared/favoritemodule/FavoriteGrid';
import Pagination from '~/components/miscellaneous/Pagination';
import SectionHeader from '~/components/layouts/SectionHeader';
import FeaturedResource from '~/components/smart/FeaturedResource';

// Skeletons
import FavoriteGridSkeleton from '~/shared/favoritemodule/skeletons/FavoriteGridSkeleton';

// Utils
import moment from 'moment';
import WidgetModule from '~/components/smart/WidgetModule.vue';

const FAVORITE_PAGE_LIMIT = 15;
export default {
    name: 'Favorites',
    components: {
        Header,
        MainLayout,
        ProfileModule,
        VerifyModule,
        FavoriteGrid,
        Pagination,
        SectionHeader,
        FeaturedResource,
        FavoriteGridSkeleton,
        NoContent,
        WidgetModule,
    },
    data: () => ({
        isLoadingFirstTime: false,
        loadingActive: false,
        loadingExpired: false,
        section: 0,
    }),
    computed: {
        favorites() {
            return this.$interfaces.favoriteModule.getActiveFavorites();
        },
        expiredFavorites() {
            return this.$interfaces.favoriteModule.getExpiredFavorites();
        },
        expiresThisWeek() {
            return this.favorites.filter(e =>
                moment(e.postingExpireTime).isSame(new Date(), 'week')
            );
        },
        expiresThisMonth() {
            return this.favorites.filter(e => {
                const m = moment(e.postingExpireTime);
                return !m.isSame(new Date(), 'week') && m.isSame(new Date(), 'month');
            });
        },
        expiresLater() {
            return this.favorites.filter(e =>
                moment(e.postingExpireTime).isSameOrAfter(moment().add(1, 'month'), 'month')
            );
        },
        expired() {
            return this.expiredFavorites.filter(e =>
                moment(e.postingExpireTime).isBefore(moment())
            );
        },
        numberOfActive() {
            return (
                this.expiresThisWeek.length +
                this.expiresThisMonth.length +
                this.expiresLater.length
            );
        },
        isActiveSection() {
            return this.section === 0;
        },
        isExpiredSection() {
            return this.section === 1;
        },
        activeTotalCount() {
            return this.$interfaces.favoriteModule.getActiveTotalCount();
        },
        expiredTotalCount() {
            return this.$interfaces.favoriteModule.getExpiredTotalCount();
        },
    },
    methods: {
        fetchActiveFavorites() {
            // Gets active favorite positions
            this.loadingActive = true;
            return this.$interfaces.candidate.favorite
                .get({
                    forceUpdate: true,
                    limit: FAVORITE_PAGE_LIMIT,
                    page: this.$interfaces.favoriteModule.getActivePage(),
                    onlyActive: true,
                })
                .then(data => {
                    if (data && data.data && data.data.results instanceof Array) {
                        this.$interfaces.favoriteModule.appendActiveFavorites(data.data.results);
                        this.$interfaces.favoriteModule.setHasNextActivePage(data.data.hasNextPage);
                        this.$interfaces.favoriteModule.setActiveTotalCount(data.data.totalCount);
                    }
                })
                .finally(() => (this.loadingActive = false));
        },
        fetchExpiredFavorites() {
            // Gets active favorite positions
            this.loadingExpired = true;
            return this.$interfaces.candidate.favorite
                .get({
                    forceUpdate: true,
                    limit: FAVORITE_PAGE_LIMIT,
                    page: this.$interfaces.favoriteModule.getExpiredPage(),
                    onlyExpired: true,
                })
                .then(data => {
                    if (data && data.data && data.data.results instanceof Array) {
                        this.$interfaces.favoriteModule.appendExpiredFavorites(data.data.results);
                        this.$interfaces.favoriteModule.setHasNextExpiredPage(
                            data.data.hasNextPage
                        );
                        this.$interfaces.favoriteModule.setExpiredTotalCount(data.data.totalCount);
                    }
                })
                .finally(() => (this.loadingExpired = false));
        },
        nextActivePage() {
            if (!this.$interfaces.favoriteModule.getHasNextActivePage() || this.loadingActive) {
                return;
            }

            this.$interfaces.favoriteModule.activePageIncrement();
            this.fetchActiveFavorites();
        },
        nextExpiredPage() {
            if (!this.$interfaces.favoriteModule.getHasNextExpiredPage() || this.loadingExpired) {
                return;
            }

            this.$interfaces.favoriteModule.expiredPageIncrement();
            this.fetchExpiredFavorites();
        },
        tabs() {
            return [
                {
                    value: 0,
                    label: this.$t('favorites.active').concat(' (', this.activeTotalCount, ')'),
                },
                {
                    value: 1,
                    label: this.$t('favorites.expired').concat(' (', this.expiredTotalCount, ')'),
                },
            ];
        },
    },
    mounted() {
        this.isLoadingFirstTime = true;
        if (this.favorites.length === 0) {
            const fetchActiveTask = this.fetchActiveFavorites();
            const fetchExpiredTask = this.fetchExpiredFavorites();
            Promise.all([fetchActiveTask, fetchExpiredTask]).finally(
                () => (this.isLoadingFirstTime = false)
            );
        } else {
            this.isLoadingFirstTime = false;
        }
    },
};
</script>

<style scoped>
.favorites-root {
    padding-bottom: 100px;
}

.tab-root {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
}
.padding {
    height: 16px;
}
</style>
